// admin-ui/src/components/FeeDistribution.tsx
// ALL RIGHTS RESERVED

/**
 * Fee Distribution Display
 * Shows fee split per Contract Section 2.3.1
 * - CXXR: 47.5% - Operations
 * - CCC: 47.5% - Infrastructure  
 * - Reserve: 5% - Dividend Pool
 */

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface FeeDistributionProps {
  total: string;
  cxxr: string;
  ccc: string;
  reserve: string;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export function FeeDistribution({ total, cxxr, ccc, reserve }: FeeDistributionProps) {
  const totalNum = parseFloat(total);
  const cxxrNum = parseFloat(cxxr);
  const cccNum = parseFloat(ccc);
  const reserveNum = parseFloat(reserve);

  // Calculate actual percentages
  const cxxrPct = totalNum > 0 ? (cxxrNum / totalNum) * 100 : 47.5;
  const cccPct = totalNum > 0 ? (cccNum / totalNum) * 100 : 47.5;
  const reservePct = totalNum > 0 ? (reserveNum / totalNum) * 100 : 5;

  return (
    <div className="fee-distribution">
      <div className="fee-total">
        <span className="label">Total Fees Collected</span>
        <span className="value">{formatCurrency(total)}</span>
      </div>

      <div className="fee-bar">
        <div 
          className="fee-segment cxxr" 
          style={{ width: `${cxxrPct}%` }}
          title={`CXXR: ${formatCurrency(cxxr)} (${cxxrPct.toFixed(1)}%)`}
        />
        <div 
          className="fee-segment ccc" 
          style={{ width: `${cccPct}%` }}
          title={`CCC: ${formatCurrency(ccc)} (${cccPct.toFixed(1)}%)`}
        />
        <div 
          className="fee-segment reserve" 
          style={{ width: `${reservePct}%` }}
          title={`Reserve: ${formatCurrency(reserve)} (${reservePct.toFixed(1)}%)`}
        />
      </div>

      <div className="fee-legend">
        <div className="legend-item">
          <div className="legend-color cxxr" />
          <div className="legend-details">
            <span className="legend-label">CXXR Operations</span>
            <span className="legend-value">{formatCurrency(cxxr)}</span>
            <span className="legend-percent">47.5%</span>
          </div>
        </div>
        <div className="legend-item">
          <div className="legend-color ccc" />
          <div className="legend-details">
            <span className="legend-label">CCC Infrastructure</span>
            <span className="legend-value">{formatCurrency(ccc)}</span>
            <span className="legend-percent">47.5%</span>
          </div>
        </div>
        <div className="legend-item">
          <div className="legend-color reserve" />
          <div className="legend-details">
            <span className="legend-label">Reserve Dividend Pool</span>
            <span className="legend-value">{formatCurrency(reserve)}</span>
            <span className="legend-percent">5%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeeDistribution;
